/**
 * Müşteri ekle / düzenle modalı.
 *
 * TEK KAYNAK: Müşteriler ekranı ve Satış/Stok > Stok Çıkışı ekranındaki "Ekle"
 * butonu AYNI bileşeni kullanır → iki yerdeki form birebir aynı kalır
 * (Ad Soyad, Telefon, E-posta, Not, Araçlar).
 *
 * Kaydetme isteği (POST/PUT /customers) bu bileşenin içindedir; çağıran taraf
 * yalnızca `onSaved(customer)` ile sonucu alır.
 *
 * ÖNEMLİ: Radix Dialog içeriği portal'a taşınır ama React ağacında hâlâ
 * çağıranın altındadır → submit olayı React'in sentetik sisteminde YUKARI
 * BALONLANIR. Stok Çıkışı ekranında bu modal dış `<form>`un içinde render
 * edildiği için `stopPropagation()` şart: aksi hâlde müşteri kaydedilirken dış
 * form da submit edilir ve "Müşteri seçin" gibi hatalı uyarılar çıkar.
 */
import { useEffect, useState } from "react";
import { api, notifyError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import { Plus, Loader2, X } from "lucide-react";

export const COUNTRY_CODES = [
  "+90", "+1", "+44", "+49", "+33", "+39", "+34", "+31", "+43", "+32",
  "+41", "+46", "+47", "+45", "+358", "+48", "+30", "+40",
];
export const COUNTRY_FLAGS = {
  "+90": "🇹🇷", "+1": "🇺🇸", "+44": "🇬🇧", "+49": "🇩🇪", "+33": "🇫🇷",
  "+39": "🇮🇹", "+34": "🇪🇸", "+31": "🇳🇱", "+43": "🇦🇹", "+32": "🇧🇪",
  "+41": "🇨🇭", "+46": "🇸🇪", "+47": "🇳🇴", "+45": "🇩🇰", "+358": "🇫🇮",
  "+48": "🇵🇱", "+30": "🇬🇷", "+40": "🇷🇴",
};

// Parse a stored phone string into { country_code, phone } so the edit dialog
// pre-fills both fields. Legacy entries without a +code default to TR (+90).
export function splitPhone(full) {
  if (!full) return { country_code: "+90", phone: "" };
  const m = String(full).trim().match(/^(\+\d{1,4})\s*(.*)$/);
  if (m && COUNTRY_CODES.includes(m[1])) return { country_code: m[1], phone: m[2] };
  return { country_code: "+90", phone: String(full).trim() };
}

export const EMPTY_CUSTOMER_FORM = {
  full_name: "",
  country_code: "+90",
  phone: "",
  email: "",
  notes: "",
  vehicles: [{ plate: "", brand: "", model: "" }],
};

/** Mevcut müşteri kaydını form durumuna çevirir (düzenleme akışı). */
export function customerToForm(c) {
  if (!c) return EMPTY_CUSTOMER_FORM;
  const vehicles = (c.vehicles && c.vehicles.length)
    ? c.vehicles.map((v) => ({
        id: v.id,
        plate: v.plate || "",
        brand: v.brand || "",
        model: v.model || "",
      }))
    : [{ plate: c.plate || "", brand: c.vehicle_brand || "", model: c.vehicle_model || "" }];
  const { country_code, phone } = splitPhone(c.phone);
  return {
    full_name: c.full_name || "",
    country_code,
    phone,
    email: c.email || "",
    notes: c.notes || "",
    vehicles,
  };
}

export default function CustomerFormDialog({ open, onOpenChange, customer, onSaved }) {
  const editingId = customer?.id || null;
  const [form, setForm] = useState(EMPTY_CUSTOMER_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(customerToForm(customer));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editingId]);

  const updateVehicle = (idx, field, val) => {
    const next = [...form.vehicles];
    next[idx] = { ...next[idx], [field]: val };
    setForm({ ...form, vehicles: next });
  };
  const addVehicle = () =>
    setForm({ ...form, vehicles: [...form.vehicles, { plate: "", brand: "", model: "" }] });
  const removeVehicle = (idx) => {
    const next = form.vehicles.filter((_, i) => i !== idx);
    setForm({ ...form, vehicles: next.length ? next : [{ plate: "", brand: "", model: "" }] });
  };

  const submit = async (e) => {
    e.preventDefault();
    // Dış form'un (ör. Stok Çıkışı) submit'ini tetiklemeyelim — bkz. dosya başı.
    e.stopPropagation();
    if (!form.full_name.trim() || !form.phone.trim()) {
      toast.error("Ad Soyad ve Telefon zorunludur");
      return;
    }
    setSaving(true);
    try {
      const phoneNumber = (form.phone || "").trim();
      const combinedPhone = phoneNumber ? `${form.country_code} ${phoneNumber}` : "";
      const { country_code, ...rest } = form;
      const payload = {
        ...rest,
        full_name: form.full_name.trim(),
        phone: combinedPhone,
        vehicles: form.vehicles.filter((v) => v.plate || v.brand || v.model),
      };
      const { data } = editingId
        ? await api.put(`/customers/${editingId}`, payload)
        : await api.post("/customers", payload);
      toast.success(editingId ? "Müşteri güncellendi" : "Müşteri eklendi");
      onOpenChange(false);
      await onSaved?.(data);
    } catch (err) {
      notifyError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">
            {editingId ? "Müşteri Düzenle" : "Yeni Müşteri"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="customer-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2 space-y-1.5">
              <Label className="label-eyebrow">Ad Soyad *</Label>
              <Input
                required
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                className="rounded-sm"
                data-testid="cust-fullname"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Telefon *</Label>
              <div className="flex gap-2">
                <Select
                  value={form.country_code}
                  onValueChange={(value) => setForm({ ...form, country_code: value })}
                >
                  <SelectTrigger className="rounded-sm w-28 shrink-0" data-testid="cust-country-code">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRY_CODES.map((code) => (
                      <SelectItem key={code} value={code}>
                        {COUNTRY_FLAGS[code]} {code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  required
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="rounded-sm flex-1 min-w-0"
                  placeholder="5XX XXX XX XX"
                  data-testid="cust-phone"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow">E-posta</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="rounded-sm"
                data-testid="cust-email"
              />
            </div>
            <div className="sm:col-span-2 space-y-1.5">
              <Label className="label-eyebrow">Not</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="rounded-sm"
                data-testid="cust-notes"
              />
            </div>
          </div>

          {/* Vehicles repeater */}
          <div className="border-t border-border pt-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="label-eyebrow">Araçlar</div>
                <p className="text-xs text-muted-foreground">Birden fazla araç ekleyebilirsiniz.</p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addVehicle}
                className="rounded-sm"
                data-testid="add-vehicle-btn"
              >
                <Plus className="h-4 w-4 mr-1" /> Araç Ekle
              </Button>
            </div>
            <div className="space-y-3">
              {form.vehicles.map((v, idx) => (
                <div
                  key={idx}
                  className="border border-border rounded-sm p-3 space-y-2 relative"
                  data-testid={`vehicle-row-${idx}`}
                >
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <Input
                      placeholder="Plaka"
                      value={v.plate}
                      onChange={(e) => updateVehicle(idx, "plate", e.target.value)}
                      className="rounded-sm uppercase font-mono"
                      data-testid={`vehicle-${idx}-plate`}
                    />
                    <Input
                      placeholder="Marka"
                      value={v.brand}
                      onChange={(e) => updateVehicle(idx, "brand", e.target.value)}
                      className="rounded-sm"
                      data-testid={`vehicle-${idx}-brand`}
                    />
                    <Input
                      placeholder="Model"
                      value={v.model}
                      onChange={(e) => updateVehicle(idx, "model", e.target.value)}
                      className="rounded-sm"
                      data-testid={`vehicle-${idx}-model`}
                    />
                  </div>
                  {form.vehicles.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeVehicle(idx)}
                      className="absolute top-2 right-2 p-1 rounded-sm hover:bg-destructive/10 hover:text-destructive"
                      data-testid={`remove-vehicle-${idx}`}
                      aria-label="Aracı kaldır"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="rounded-sm"
            >
              İptal
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="rounded-sm bg-foreground text-background hover:bg-foreground/90"
              data-testid="cust-submit"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editingId ? "Güncelle" : "Kaydet"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
